// Pure crop geometry for the profile-photo panel. The viewport is a square
// of side `view`; the image is scaled to cover it, then multiplied by zoom.
// Offsets are the image centre's displacement from the viewport centre.

export const MIN_ZOOM = 1;
export const MAX_ZOOM = 3;
export const EXPORT_SIZE = 256;
export const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
export const ACCEPTED_TYPES = ["image/png", "image/jpeg"];

export function clampZoom(zoom) {
  const z = Number(zoom);
  if (!Number.isFinite(z)) return MIN_ZOOM;
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));
}

export function coverScale(width, height, view) {
  if (!(width > 0) || !(height > 0) || !(view > 0)) return 0;
  return view / Math.min(width, height);
}

export function clampOffset(offset, width, height, view, zoom) {
  const scale = coverScale(width, height, view) * clampZoom(zoom);
  const maxX = Math.max(0, (width * scale - view) / 2);
  const maxY = Math.max(0, (height * scale - view) / 2);
  const clamp = (v, m) => Math.min(m, Math.max(-m, Number(v) || 0)) || 0;
  return { x: clamp(offset?.x, maxX), y: clamp(offset?.y, maxY) };
}

/** Source rectangle (natural pixels) visible inside the square viewport. */
export function sourceRect(width, height, view, zoom, offset) {
  const scale = coverScale(width, height, view) * clampZoom(zoom);
  if (!scale) return { sx: 0, sy: 0, size: 0 };
  const o = clampOffset(offset, width, height, view, zoom);
  const left = view / 2 + o.x - (width * scale) / 2;
  const top = view / 2 + o.y - (height * scale) / 2;
  return { sx: (-left / scale) || 0, sy: (-top / scale) || 0, size: view / scale };
}

export function validatePhotoFile(file) {
  if (!file) return "Choose an image to upload.";
  if (!ACCEPTED_TYPES.includes(file.type)) return "Use a PNG or JPEG image.";
  if (file.size > MAX_PHOTO_BYTES) return "Image must be 5MB or smaller.";
  return null;
}

/** custom -> Okta -> legacy photo_url (only when new fields are absent). */
export function resolveAccountPhotoUrl(user) {
  if (!user) return null;
  if ("custom_photo_url" in user || "okta_photo_url" in user) {
    return user.custom_photo_url ?? user.okta_photo_url ?? null;
  }
  return user.photo_url ?? null;
}
